// BCM-898 · Goods Receipt Dashboard — the Action-section gate (shared).
//
// The Action box for a batch is decided from THREE signals only (per the
// 2026-08-21 review): the batch STATUS, the FLAGS/fields on the batch row, and
// the FULFILLMENT of the batch's pending requirements. The progress-bar
// requirement *label text* is NOT consulted for gating — matching on label
// strings was brittle (the same requirement is worded differently per process
// flow, and defects D10/D11 mean some labels never reconcile). Everything here
// is FRONTEND-ONLY — no backend change backs any of it; every signal below comes
// from data controllers/BMS/fetchDeliverySchedulebatchDetailsNew.js and
// fetchBatchesDirect.js already return.
//
//   render(PA) = ( tier1 flag  OR  tier2 status[+row field] )
//                AND NOT ( pending requirements PRESENT AND ALL fulfilled )
//
//   Tier 1 — per-action flags on the fetchBatchesDirect row (authoritative,
//            never suppressed by fulfillment). reviewPreShipmentFieldsReqd /
//            reviewPostShipmentFieldsReqd / reviewAllocationForConsumptionReqd
//            are the same-shaped siblings of reviewPostMFGFieldsReqd /
//            reviewConsumptionReqd — added here so statuses 45/50 (pre/post-
//            shipment field review) resolve to something instead of falling
//            through to no action.
//   Tier 2 — SASLBatchStatusID; the several statuses that mean different things in
//            different process flows are split by fields ALREADY on the row / in
//            the loaded A2 detail — no processFlowId and no label:
//              · 39  pending-requirement present → PA7 (flows 1-4). Flow 5 has no
//                    pre-shipment stage at all, so its A2 detail is used to detect
//                    it: progressBar.statusSequence never contains a PRE_SHIPMENT
//                    step for flow 5 (shared/statusConfig.ts) — that step field is
//                    already on the unmodified progress-bar payload, so this needs
//                    no processFlowId and no backend change. Flow 5 → PA8 (it runs
//                    its pre-GRN gate from this status).
//              · 47  BOGeneratedGRNNumber present → post-GRN PA12, else pre-GRN PA8
//              · 49  the progress bar's UPCOMING status is "Batch Released By BMS
//                    QA" → QA-flow PA10, else post-shipment PA12 / none.
//              · 41  partialShippingDetails is a non-empty array → PA11, else none.
//              · 38  Material Not Expired (next: Consumed Material Matching) → PA5,
//                    consumed material not matching the BMS PO BoM material.
//              · 55 Tier 1 already covers the case where reviewConsumptionReqd /
//                    reviewAllocationForConsumptionReqd is set. Reaching Tier 2
//                    here means those flags are OFF, and the batch is still on 55
//                    — the workflow only ever writes 37 when stock check is NOT
//                    required, so staying on 55 with review already done means
//                    Step 7's expiry loop is running (same pending action as 37).
//   Suppressor — progressBar.pendingRequirements every `fulfilled === true`
//            (aggregate boolean; no label is read)
//
// A batch has exactly one *current* pending action. This resolver returns it,
// along with a human-readable label for the collapsed row (our own PA_LABEL map,
// NOT the progress-bar requirement label). `detail` (the
// fetchDeliveryScheduleBatchDetails payload) is optional — it supplies the
// fulfillment list and the status sequence used for the flow-5 test; without it
// the resolver still returns a best guess from the row (slide 5: the label must
// show before expansion).
import { STATUS, PA, DOC_CATEGORY } from "./constants";
import { isFlagTrue } from "./formatters";
import { reviewSummary, pa2CategoryFor } from "./reviewDocs";

// --- Fulfillment helpers (label-free) -------------------------------------
const requirements = (detail) =>
  (detail && detail.progressBar && detail.progressBar.pendingRequirements) || [];

// The single fulfillment signal used for gating: the batch's pending
// requirements are PRESENT and every one is fulfilled === true. A missing/empty
// list never suppresses; a constant-false requirement can never suppress (safe
// direction — the card stays up). No label is inspected.
const allRequirementsFulfilled = (detail) => {
  const reqs = requirements(detail);
  return reqs.length > 0 && reqs.every((r) => r && r.fulfilled === true);
};

// A row/detail string field that actually carries a value.
const hasValue = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "";

const hasNonEmptyArray = (v) => Array.isArray(v) && v.length > 0;

// The status name the batch is about to move INTO, read from the progress bar's
// status sequence. `state === 'upcoming'` marks it explicitly; when the payload
// carries only completed/current/future, the upcoming one is the entry right
// after the current status (same rule BatchProgressBar.analyse() uses).
const QA_RELEASE_STATUS_NAME = "Batch Released By BMS QA";
const upcomingStatusName = (detail) => {
  const seq =
    detail && detail.progressBar && detail.progressBar.statusSequence;
  if (!Array.isArray(seq) || seq.length === 0) return null;
  const sorted = [...seq].sort(
    (a, b) => (a.sequence || 0) - (b.sequence || 0)
  );
  let idx = sorted.findIndex((s) => s && s.state === "upcoming");
  if (idx === -1) {
    const cur = sorted.findIndex((s) => s && s.state === "current");
    idx = cur === -1 ? -1 : cur + 1;
  }
  if (idx < 0 || idx >= sorted.length) return null;
  const s = sorted[idx] || {};
  return s.statusName || s.statusDisplayName || null;
};

// Flow 5 is the only process flow with no pre-shipment stage at all, so its
// statusSequence never contains an entry with step 'PRE_SHIPMENT'
// (temporal-workflows/.../shared/statusConfig.ts — flows 1-4 all include a
// 'Pre-Shipment Documents Uploaded' entry, flow 5's array has none). The `step`
// field is already present on the unmodified progress-bar payload, so this is a
// frontend-only, no-processFlowId way to tell flow 5 apart at status 39.
const isFlow5 = (detail) => {
  const seq = detail && detail.progressBar && detail.progressBar.statusSequence;
  if (!Array.isArray(seq) || seq.length === 0) return false;
  return !seq.some((s) => s && s.step === "PRE_SHIPMENT");
};

// Human labels for the collapsed row (slide 5 "Pending-action label"). This is
// our own mapping keyed by the resolved PA — it is not the progress-bar label.
// PA8 and PA9 are per-flow: the same batch is on either the IBD or the GRN
// path, never both, so the row can name exactly the one that is pending. The
// combined "IBD / GRN" wording stays as the fallback when the flow signal
// isn't on the row yet (detail not loaded, no matching flag).
const PA8_IBD_LABEL = "Pre-IBD check failed";
const PA8_GRN_LABEL = "Pre-GRN check failed";
const PA8_LABEL_FALLBACK = "Pre-IBD / Pre-GRN check failed";
const PA9_IBD_LABEL = "IBD generation pending";
const PA9_GRN_LABEL = "GRN generation pending";
const PA9_LABEL_FALLBACK = "IBD / GRN generation pending";

export const PA_LABEL = {
  // BCM-898 bug fix: "Manufacturing" must be capitalized, per the FS wording
  // ("Post-Manufacturing Documents Not Received") and to match the expanded
  // section's own card header (PA1PostMfgDocuments.jsx: "Post-Manufacturing
  // documents are not yet received") — the two were visible two lines apart
  // on the same screen and disagreed on casing.
  [PA.PA1]: "Post-Manufacturing documents pending",
  // Collapsed-row wording for ANY field review (ASN or not): whether the batch is
  // ASN is only known once its detail loads — see pa2Label.
  [PA.PA2]: "Review pending",
  [PA.PA3]: "Consumption review pending",
  [PA.PA4]: "Material Batch Expired",
  // Same wording View Batch Info shows as the batch's pending action at status 38.
  [PA.PA5]: "Consumed Material is not matching BMS PO BoM Material",
  [PA.PA6]: "Variance/mismatch approval pending",
  [PA.PA7]: "Pre-Shipment documents pending",
  [PA.PA8]: PA8_LABEL_FALLBACK,
  [PA.PA9]: PA9_LABEL_FALLBACK,
  [PA.PA10]: "Batch QA Release Pending",
  [PA.PA11]: "Batch QA Release Rejected",
  [PA.PA12]: "Post-Shipment documents pending",
};

// GRN vs IBD flow — the same direction PA8PreIBDGRNCheck.isGRNFlow(detail) uses
// (the loaded A2 detail's progressBar.statusSequence carries the flow's fixed
// template, so presence of "GRN Generated" vs "IBD Generated" identifies it
// even for upcoming steps). Row-only signals are the fallback for the
// collapsed-row label before detail loads: showGenerate/CreateIBDActionButton
// fire per flow; BOGeneratedGRNNumber being present on the row means the batch
// has already gone through GRN generation, so its pre-check is the GRN one.
const flowFromRow = (row) => {
  if (!row) return null;
  if (isFlagTrue(row.showGenerateGRNActionButton) || hasValue(row.BOGeneratedGRNNumber)) return "GRN";
  if (isFlagTrue(row.showCreateIBDActionButton)) return "IBD";
  // BCM-898 bug fix: CMO_ASN_COMPLETED (47) is unambiguous without needing
  // detail at all — fromStatus's own case 47 branch only ever resolves to PA8
  // for this exact combination (status 47 + no GRN number yet is PF3's own
  // pre-GRN gate; PF4's "GRN already generated" case routes to PA12 instead,
  // never PA8) — so reaching PA8 via that status means GRN, always. This was
  // previously unresolvable from the row alone, so the collapsed list label
  // fell back to the merged "Pre-IBD / Pre-GRN check failed" wording instead
  // of "Pre-GRN check failed". Status 56 (PRE_SHIPMENT_VALIDATED) is NOT
  // included here — it's genuinely ambiguous between PF1 (IBD) and PF2/PF4
  // (GRN) without the loaded detail, so it still falls through to the merged
  // label until the row is expanded.
  if (row.SASLBatchStatusID === STATUS.CMO_ASN_COMPLETED && !hasValue(row.BOGeneratedGRNNumber)) return "GRN";
  return null;
};
const flowFromDetail = (detail) => {
  const seq = (detail && detail.progressBar && detail.progressBar.statusSequence) || [];
  if (seq.some((s) => s && s.statusName === "GRN Generated")) return "GRN";
  if (seq.some((s) => s && s.statusName === "IBD Generated")) return "IBD";
  return null;
};

// PA2's row label, by how much is known about the batch:
//   · row collapsed, or its detail not loaded yet → PA_LABEL[PA2], "Review
//     pending", the same for every batch — the row alone can't say whether it is
//     an ASN. (resolvePendingAction withholds `detail` from collapsed rows; the
//     table keeps a row's detail after it collapses.)
//   · expanded, detail loaded, ASN (IDoc-sourced documents from the CMO ERP file
//     transfer, no per-field confidence) → "Post-MFG review pending" for the
//     post-manufacturing ASN, "Delivery ASN review pending" for the shipment ASNs;
//   · expanded, detail loaded, anything else (OCR'd documents) → "N Low
//     Confidence Fields", or "Review not marked as complete" when none are left
//     to review (every field reviewed, only "Mark Review as Complete" remains).
export const DELIVERY_ASN_LABEL = "Delivery ASN review pending";
export const POST_MFG_REVIEW_PENDING_LABEL = "Post-MFG review pending";
const REVIEW_NOT_MARKED_COMPLETE_LABEL = "Review not marked as complete";

const pa2Label = (row, detail) => {
  if (!detail) return PA_LABEL[PA.PA2];
  const category = pa2CategoryFor(row);
  const { isAsn, count } = reviewSummary(detail, category);
  if (isAsn) {
    return category === DOC_CATEGORY.POST_MANUFACTURING
      ? POST_MFG_REVIEW_PENDING_LABEL
      : DELIVERY_ASN_LABEL;
  }
  return count > 0
    ? `${count} Low Confidence Field${count === 1 ? "" : "s"}`
    : REVIEW_NOT_MARKED_COMPLETE_LABEL;
};

// Choose the row-specific label for a resolved PA. Only PA2, PA8 and PA9 vary
// today; every other action returns its PA_LABEL entry unchanged. Used by
// BatchRow when the ActionSection PA9 gate settles asynchronously and by the
// resolver's Tier-1 PA2/PA9 branches + Tier-2 PA8 branches so the two label
// paths always agree.
export function labelFor(pa, row, detail) {
  if (pa === PA.PA2) return pa2Label(row, detail);
  if (pa === PA.PA9 && row) {
    const ibd = isFlagTrue(row.showCreateIBDActionButton);
    const grn = isFlagTrue(row.showGenerateGRNActionButton);
    if (ibd && !grn) return PA9_IBD_LABEL;
    if (grn && !ibd) return PA9_GRN_LABEL;
  }
  if (pa === PA.PA8) {
    const flow = flowFromRow(row) || flowFromDetail(detail);
    if (flow === "IBD") return PA8_IBD_LABEL;
    if (flow === "GRN") return PA8_GRN_LABEL;
  }
  return PA_LABEL[pa] || "";
}

const NONE = { pa: null, pas: [], label: "", needsDetail: false };
const mk = (pa, row, detail) => ({ pa, pas: [pa], label: labelFor(pa, row, detail), needsDetail: false });
const mkGuess = (pa, row, detail) => ({ pa, pas: [pa], label: labelFor(pa, row, detail), needsDetail: true });

// Tier 2: status (+ row fields / loaded detail) → candidate action. No
// flags-vs-flags here, no labels. `detail` is the fetchDeliveryScheduleBatchDetails
// payload — statuses 39/49/41 read from it where present; without it a best guess
// is returned (needsDetail:true) so the collapsed-row label still shows.
function fromStatus(status, row, detail) {
  const reqCount = requirements(detail).length;
  const hasDetail = !!detail;
  switch (status) {
    case STATUS.PRODUCTION_COMPLETE: // 3
      return mk(PA.PA1);

    case STATUS.STOCK_CHECK_NOT_REQUIRED: // 37
      return mk(PA.PA4);

    case STATUS.POST_MFG_VALIDATED: // 55 — reached only once Tier 1's consumption-
      // review flags are already off; the workflow only advances past 55 to 37
      // when stock check is NOT required, so still being on 55 at this point
      // means Step 7's expiry loop is running (same pending action as 37).
      return mk(PA.PA4);

    case STATUS.MATERIAL_NOT_EXPIRED: // 38 — "Material Not Expired", next status
      // "Consumed Material Matching": the pending action is the consumed material
      // not matching the BMS PO BoM material (same as View Batch Info). Subject to
      // the fulfillment suppressor below like every other Tier 2 action.
      return mk(PA.PA5);

    case STATUS.SPECS_APPROVED: // 39 — PA7 (PF 1-4) / PA8 pre-GRN gate (PF5, no pre-shipment stage)
      if (hasDetail) {
        if (isFlow5(detail)) return mk(PA.PA8, row, detail);
        return reqCount === 0 ? NONE : mk(PA.PA7);
      }
      return mkGuess(PA.PA7);

    case STATUS.PRE_SHIPMENT_VALIDATED: // 56 — pre-IBD (PF1) / pre-GRN (PF2,4)
      return mk(PA.PA8, row, detail);

    case STATUS.CMO_ASN_COMPLETED: // 47 — pre-GRN (PF3, no GRN yet) vs post-shipment (PF4, GRN done)
      return hasValue(row.BOGeneratedGRNNumber) ? mk(PA.PA12) : mk(PA.PA8, row, detail);

    case STATUS.PRE_IBD_COMPLETED: // 108
    case STATUS.PRE_GRN_COMPLETED: // 109
    case STATUS.PARTIAL_GRN: // 60
    case STATUS.PARTIAL_IBD: // 61
      // Generation is flag-driven (Tier 1). If the flag is not set the batch is
      // not actually ready — no card.
      return NONE;

    case STATUS.GRN_GENERATED: // 49 — QA-flow (PF4) vs post-shipment (PF2,3) vs final (PF5)
      if (!hasDetail) {
        // Pre-detail best guess for the collapsed-row label; refined once A2 loads.
        return hasValue(row.qaStatusID) ? mkGuess(PA.PA10) : mkGuess(PA.PA12);
      }
      // PA10 (Batch QA release pending) renders when the progress bar's UPCOMING
      // status is "Batch Released By BMS QA" (the PF4 QA-release step).
      if (upcomingStatusName(detail) === QA_RELEASE_STATUS_NAME) return mk(PA.PA10);
      return reqCount === 0 ? NONE : mk(PA.PA12);

    case STATUS.IBD_GENERATED: // 48
    case STATUS.BATCH_RELEASED_BY_QA: // 46
      return mk(PA.PA12);

    case STATUS.BATCH_NOT_ACCEPTED: // 41 — PA11 only when partialShippingDetails is a non-empty array
      if (!hasDetail) return mkGuess(PA.PA11);
      return hasNonEmptyArray(detail.partialShippingDetails) ? mk(PA.PA11) : NONE;

    default:
      return NONE;
  }
}

/**
 * @param {object} row     one fetchBatchesDirect batch row
 * @param {object} [detail] the fetchDeliveryScheduleBatchDetails payload (adds
 *                          progressBar.pendingRequirements for the fulfillment gate
 *                          and progressBar.statusSequence for the flow-5 test)
 * @param {{ expanded?: boolean }} [opts] `expanded: false` (default true) marks the
 *                          row as collapsed: PA2's label then stays "Review
 *                          pending" even though the table still holds the row's
 *                          detail after a collapse. No other action's label or
 *                          decision depends on it.
 * @returns {{ pa: string|null, pas: string[], label: string, needsDetail: boolean }}
 *          `pas` is always a single-item array today (one action per batch); kept
 *          as a list so callers don't need to special-case a future multi-action
 *          case. `needsDetail` = true when the row alone cannot fully disambiguate
 *          and the caller should refine once the batch detail is loaded.
 */
export function resolvePendingAction(row, detail, { expanded = true } = {}) {
  if (!row) return NONE;

  const status = row.SASLBatchStatusID;
  const cleared = allRequirementsFulfilled(detail); // fulfillment, label-free

  // ── Tier 1: per-action flags (authoritative; never suppressed). ───────────
  // showGenerate/showCreate are the SAME gate the Batch Execution buttons use;
  // when either is true the batch is ready for generation regardless of status.
  if (
    isFlagTrue(row.showGenerateGRNActionButton) ||
    isFlagTrue(row.showCreateIBDActionButton)
  ) {
    return mk(PA.PA9, row);
  }
  // Post-MFG / pre-shipment / post-shipment field review — one PA2 action; its
  // label (pa2Label) is "Review pending" while the row is collapsed or its detail
  // hasn't loaded, then — expanded only — the batch's own wording (ASN vs.
  // "N Low Confidence Fields").
  if (
    isFlagTrue(row.reviewPostMFGFieldsReqd) ||
    isFlagTrue(row.reviewPreShipmentFieldsReqd) ||
    isFlagTrue(row.reviewPostShipmentFieldsReqd)
  ) {
    return mk(PA.PA2, row, expanded ? detail : undefined);
  }
  if (
    isFlagTrue(row.reviewConsumptionReqd) ||
    isFlagTrue(row.reviewAllocationForConsumptionReqd)
  ) {
    return mk(PA.PA3);
  }
  if (isFlagTrue(row.reviewVarianceReqd) || status === STATUS.SPECS_NOT_MATCHING) {
    // PA6 clears when the (live) specs requirement is fulfilled.
    return cleared ? NONE : mk(PA.PA6);
  }

  // ── Tier 2: status + row fields / loaded detail, then the fulfillment suppressor. ─
  const candidate = fromStatus(status, row, detail);
  // BCM-898 bug fix: PA8 is exempt from the fulfillment suppressor. PA8 runs
  // its OWN live pre-IBD/pre-GRN check (checkForConsumptionBatchExpiry) once
  // mounted — that live call is the authority on whether the batch can post,
  // not this pendingRequirements snapshot. Suppressing PA8 here whenever
  // pendingRequirements already reads all-fulfilled hid the whole batch-
  // expanded section before PA8 ever got to run its own check (seen on a Flow
  // 5 batch in Specs Approved status, where fromStatus above always returns
  // PA8 unconditionally — same as it does at statuses 56 and 47). Every other
  // Tier 2 action keeps the suppressor exactly as before.
  if (candidate.pa && candidate.pa !== PA.PA8 && cleared) return NONE;
  return candidate;
}

export { allRequirementsFulfilled };
